﻿import React from "react";
import {
  ActivityItemProvider,
  useActivitiesData,
} from "./ActivitiesDataContext";

const ListDepthContext = React.createContext<number>(0);

const ActivitiesList = {
  fields: {
    limitText: { type: "text" as const },
    emptyMessage: { type: "text" as const },
    cardChrome: {
      type: "radio" as const,
      options: [
        { label: "Card chrome", value: true },
        { label: "No chrome", value: false },
      ],
    },
    item: {
      type: "slot" as const,
      allow: [
        "ActivityHeader",
        "ActivityTile",
        "ActivitySparkline",
        "ActivityRow",
        "HeadingBlock",
        "TextBlock",
        "Container",
      ],
    },
  },
  defaultProps: {
    limitText: "",
    emptyMessage: "No activities found yet.",
    cardChrome: false,
    item: [
      {
        type: "Container",
        props: {
          className: "activities-card",
          padding: {
            mode: "set",
            input: "uniform",
            setUniform: "clamp(8px, 2vw, 16px)",
          },
          content: [
            { type: "ActivityHeader", props: {} },
            {
              type: "Container",
              props: {
                fontSize: {
                  mode: "scaled",
                  minFont: "10px",
                  maxFont: "14px",
                  minViewport: "320px",
                  maxViewport: "1200px"
                },
                content: [
                  {
                    type: "ActivityRow",
                    props: {
                      columns: 4,
                      gap: { mode: "set", value: "1em" },
                      content: [
                        {
                          type: "ActivityTile",
                          props: {
                            metric: "zone",
                            label: "Zone",
                            colorClass: "from-indigo-500 to-purple-600",
                            padding: {
                              mode: "set",
                              input: "sym",
                              setY: "1em",
                              setX: "0.75em"
                            }
                          },
                        },
                        {
                          type: "ActivityTile",
                          props: {
                            metric: "duration",
                            label: "Duration",
                            colorClass: "from-slate-500 to-slate-600",
                            padding: {
                              mode: "set",
                              input: "sym",
                              setY: "1em",
                              setX: "0.75em"
                            }
                          },
                        },
                        {
                          type: "ActivityTile",
                          props: {
                            metric: "epoc",
                            label: "EPOC",
                            colorClass: "from-pink-500 to-rose-600",
                            padding: {
                              mode: "set",
                              input: "sym",
                              setY: "1em",
                              setX: "0.75em"
                            }
                          },
                        },
                        {
                          type: "ActivityTile",
                          props: {
                            metric: "trimp",
                            label: "TRIMP",
                            colorClass: "from-orange-500 to-amber-600",
                            padding: {
                              mode: "set",
                              input: "sym",
                              setY: "1em",
                              setX: "0.75em"
                            }
                          },
                        },
                      ],
                    },
                  },
                ],
              },
            },
            { type: "ActivitySparkline", props: {} },
          ],
        },
      },
    ],
  },
  render: (props: any) => {
    const limitText = (props?.limitText ?? "") as string;
    const emptyMessage = (props?.emptyMessage ?? "No activities found yet.") as string;
    const cardChrome = !!(props?.cardChrome ?? true);
    const Item = (props as any).item as React.ComponentType<any>;

    const { activities } = useActivitiesData();
    const depth = React.useContext(ListDepthContext);

    const limit = Math.max(
      0,
      Number.isFinite(Number(limitText)) ? parseInt(limitText, 10) : 0
    );
    const list =
      limit > 0 ? activities.slice(0, Math.min(limit, activities.length)) : activities;

    // Guard against recursive rendering if corrupted data inserts ActivitiesList into its own slot
    if (depth > 0) {
      return (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
          Rendering prevented: ActivitiesList detected recursively inside its own item slot (depth {depth}). Replace the slot content with non-list blocks.
        </div>
      );
    }

    if (!list.length) {
      return (
        <div className="p-6 bg-yellow-50 border border-yellow-200 rounded text-yellow-800">
          {emptyMessage}
        </div>
      );
    }

    return (
      <ListDepthContext.Provider value={depth + 1}>
        <div className="space-y-4">
          {list.map((a, i) => {
            const inner = (
              <ActivityItemProvider item={a} index={i}>
                <Item />
              </ActivityItemProvider>
            );

            if (cardChrome) {
              // Legacy wrapper for existing layouts; new layouts should compose a Container with activities-card
              return (
                <div key={a.activityGuid} className="activities-card overflow-hidden">
                  <div style={{ padding: 'clamp(8px, 2vw, 16px)' }}>{inner}</div>
                </div>
              );
            }

            return <div key={a.activityGuid}>{inner}</div>;
          })}
        </div>
      </ListDepthContext.Provider>
    );
  },
};

export default ActivitiesList;


